import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useInventory } from "@/contexts/InventoryContext";
import { cn } from "@/lib/utils";
import { Search, Plus, AlertTriangle, Package, Zap, Sparkles } from "lucide-react";
import { StockItem, ItemType, ServiceBillingMethod } from "@/lib/accounting/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

export interface SelectedItemPayload {
  id?: string;
  name: string;
  itemType: ItemType;
  hsnCode?: string;
  sacCode?: string;
  rate?: number;
  unit?: string;
  gstRate?: number;
  currentQty?: number;
  isLowStock?: boolean;
  billingMethod?: string;
  isNew?: boolean;
}

interface ItemAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectItem?: (item: SelectedItemPayload) => void;
  onAdvanceNext?: () => void;
  onEmptyRowEnter?: () => void;
  filterType?: "all" | "goods" | "service";
  transactionType?: "Sales" | "Purchase";
  placeholder?: string;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  allocatedQuantities?: Record<string, number>;
  currentRowId?: string;
}

function scoreMatch(item: StockItem, query: string): number {
  const q = query.toLowerCase().trim();
  const name = (item.name || "").toLowerCase();
  const hsn = (item.hsnCode || "").toLowerCase();
  const sac = (item.sacCode || "").toLowerCase();
  const group = (item.stockGroup || "").toLowerCase();

  if (name === q) return 100;
  if (name.startsWith(q)) return 85;
  const words = name.split(/[\s\-\/]+/);
  if (words.some((w) => w.startsWith(q))) return 65;
  if (name.includes(q)) return 45;
  if (hsn.startsWith(q)) return 40;
  if (sac.startsWith(q)) return 40;
  if (hsn.includes(q) || sac.includes(q)) return 30;
  if (group.includes(q)) return 20;

  let qi = 0;
  for (let i = 0; i < name.length && qi < q.length; i++) {
    if (name[i] === q[qi]) qi++;
  }
  if (qi === q.length) return 15;
  return 0;
}

function highlightMatch(text: string, query: string) {
  if (!query || !query.trim()) return text;
  const cleanQ = query.trim();
  const idx = text.toLowerCase().indexOf(cleanQ.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent/30 text-foreground rounded-sm px-0.5">{text.slice(idx, idx + cleanQ.length)}</mark>
      {text.slice(idx + cleanQ.length)}
    </>
  );
}

const GST_RATES = [0, 5, 12, 18, 28];
const GOODS_UNITS = ["Nos", "Pcs", "Kg", "Gm", "Ltr", "Mtr", "Ft", "Sq.Ft", "Box", "Set", "Pair", "Bag", "Ton", "Quintal"];
const SERVICE_UNITS = ["Hours", "Days", "Months", "Years", "Projects", "Services", "Sessions", "Visits", "Nos", "Units"];

interface QuickItemForm {
  name: string;
  itemType: ItemType;
  hsnCode: string;
  sacCode: string;
  billingMethod: ServiceBillingMethod;
  gstRate: number;
  unit: string;
  sellingPrice: string;
  purchasePrice: string;
  description: string;
}

const emptyForm = (name = ""): QuickItemForm => ({
  name,
  itemType: "goods",
  hsnCode: "",
  sacCode: "",
  billingMethod: "one-time",
  gstRate: 18,
  unit: "Nos",
  sellingPrice: "",
  purchasePrice: "",
  description: "",
});

export function ItemAutocomplete({
  value,
  onChange,
  onSelectItem,
  onAdvanceNext,
  onEmptyRowEnter,
  filterType = "all",
  transactionType = "Sales",
  placeholder = "Search goods or services...",
  className,
  inputRef: externalRef,
  allocatedQuantities,
  currentRowId,
}: ItemAutocompleteProps) {
  const { stockItems, stockGroups, addStockItem } = useAccounting();
  const { getBatchesByItem } = useInventory();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [form, setForm] = useState<QuickItemForm>(emptyForm());
  const internalRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Authoritative real-time item availability calculation
  const getItemAvailability = useCallback(
    (item: StockItem) => {
      const isService = item.itemType === "service";
      const isSales = transactionType === "Sales";

      let allocatedInOtherRows = 0;
      if (isSales && !isService && allocatedQuantities) {
        allocatedInOtherRows =
          allocatedQuantities[item.id] ??
          allocatedQuantities[item.name.trim().toLowerCase()] ??
          0;
      }

      const persistedStock = Math.max(0, Number(item.currentQty || 0));
      const availableStock = isService
        ? Infinity
        : isSales
        ? Math.max(0, Math.round((persistedStock - allocatedInOtherRows) * 100) / 100)
        : persistedStock;

      const isOutOfStock = !isService && isSales && availableStock <= 0;
      const isFullyAllocated = !isService && isSales && persistedStock > 0 && allocatedInOtherRows >= persistedStock;
      const isLowStock = !isService && isSales
        ? (availableStock > 0 && !!(item.reorderLevel && availableStock <= item.reorderLevel))
        : (!isService && !!(item.reorderLevel && persistedStock <= item.reorderLevel));

      return {
        isService,
        isSales,
        persistedStock,
        allocatedInOtherRows,
        availableStock,
        isOutOfStock,
        isFullyAllocated,
        isLowStock,
      };
    },
    [transactionType, allocatedQuantities]
  );

  // Combine external and internal ref
  const setCombinedRef = useCallback(
    (el: HTMLInputElement | null) => {
      internalRef.current = el;
      if (typeof externalRef === "function") {
        externalRef(el);
      } else if (externalRef && typeof externalRef === "object") {
        (externalRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    },
    [externalRef]
  );

  const filtered = useMemo(() => {
    const q = (value || "").trim();
    const scored: { item: StockItem; score: number }[] = [];

    for (const item of stockItems) {
      if (filterType === "goods" && item.itemType === "service") continue;
      if (filterType === "service" && item.itemType !== "service") continue;

      if (q.length === 0) {
        scored.push({ item, score: 1 });
      } else {
        const score = scoreMatch(item, q);
        if (score > 0) scored.push({ item, score });
      }
    }

    if (q.length === 0) {
      scored.sort((a, b) => (a.item.name || "").localeCompare(b.item.name || ""));
    } else {
      scored.sort((a, b) => b.score - a.score || (a.item.name || "").localeCompare(b.item.name || ""));
    }
    return scored.slice(0, 15);
  }, [value, stockItems, filterType]);

  const hasQuery = (value || "").trim().length > 0;
  const exactMatch = useMemo(() => {
    if (!hasQuery) return null;
    const q = value.trim().toLowerCase();
    return stockItems.find((s) => s.name.trim().toLowerCase() === q) || null;
  }, [hasQuery, value, stockItems]);

  const showDropdown = isOpen && (filtered.length > 0 || hasQuery);
  const totalOptions = filtered.length + (hasQuery && !exactMatch ? 1 : 0);
  const createOptionIndex = filtered.length;

  const selectItem = useCallback(
    (item: StockItem) => {
      const avail = getItemAvailability(item);

      // Block selection if product is out of stock in a sales transaction
      if (avail.isSales && !avail.isService && avail.availableStock <= 0) {
        toast({
          title: "Out of Stock",
          description: avail.isFullyAllocated
            ? `All ${avail.persistedStock} available units of "${item.name}" are already selected in other rows of this invoice.`
            : `"${item.name}" has 0 available units in stock and cannot be added to a sale invoice.`,
          variant: "destructive",
        });
        return;
      }

      const isService = item.itemType === "service";
      const isLowStock = avail.isLowStock;
      const activeBatches = isService ? [] : getBatchesByItem(item.id).filter((b) => b.status === "Active");

      let effectiveRate = 0;
      if (transactionType === "Purchase") {
        effectiveRate = Number(item.lastPurchaseRate ?? item.costRate ?? item.currentRate ?? 0);
      } else {
        effectiveRate = Number(item.lastSellingRate ?? (item.sellingRateVerified ? item.standardRate : null) ?? 0);
      }
      const effectiveGstRate = item.gstRate !== undefined && item.gstRate !== null ? Number(item.gstRate) : 18;
      const effectiveHsn = isService ? (item.sacCode || item.hsnCode || "") : (item.hsnCode || "");
      const effectiveSac = isService ? (item.sacCode || item.hsnCode || "") : "";
      const effectiveUnit = item.unit || (isService ? "Services" : "Nos");

      onChange(item.name);
      onSelectItem?.({
        id: item.id,
        name: item.name,
        itemType: item.itemType || (isService ? "service" : "goods"),
        hsnCode: effectiveHsn,
        sacCode: effectiveSac,
        rate: effectiveRate,
        unit: effectiveUnit,
        gstRate: effectiveGstRate,
        currentQty: isService ? 0 : avail.availableStock,
        isLowStock,
        billingMethod: item.billingMethod,
        isNew: false,
      });

      setIsOpen(false);

      if (!isService && isLowStock) {
        toast({
          title: "⚠️ Low Stock Alert",
          description: `"${item.name}" has only ${avail.availableStock} ${item.unit} left available (${avail.persistedStock} total in inventory).`,
          variant: "destructive",
        });
      }

      onAdvanceNext?.();
    },
    [getItemAvailability, transactionType, onChange, onSelectItem, onAdvanceNext, getBatchesByItem, toast]
  );

  // Commit typed text as an item (reuses existing if name matches; otherwise handles intelligent catalogue linking)
  const commitItem = useCallback(
    (nameToCommit: string) => {
      const trimmed = nameToCommit.trim();
      if (!trimmed) {
        setIsOpen(false);
        if (onEmptyRowEnter) {
          onEmptyRowEnter();
        } else if (onAdvanceNext) {
          onAdvanceNext();
        }
        return;
      }

      // Check if exact match exists in existing catalogue
      const match = stockItems.find((s) => s.name.trim().toLowerCase() === trimmed.toLowerCase());
      if (match) {
        const avail = getItemAvailability(match);
        if (avail.isSales && !avail.isService && avail.availableStock <= 0) {
          toast({
            title: "Out of Stock",
            description: avail.isFullyAllocated
              ? `All ${avail.persistedStock} units of "${match.name}" are already selected in other rows of this invoice.`
              : `"${match.name}" has 0 available units in stock and cannot be added to a sale invoice.`,
            variant: "destructive",
          });
          return;
        }
        selectItem(match);
        return;
      }

      // If in Sales and user typed a physical item that does not exist in inventory:
      if (transactionType === "Sales" && filterType !== "service") {
        toast({
          title: "Item Not In Stock",
          description: `"${trimmed}" does not exist in inventory with available stock. Physical goods cannot be sold without available inventory. Record a purchase first or switch to Service.`,
          variant: "destructive",
        });
        return;
      }

      // Genuinely new catalogue item -> notify parent to auto-create and link
      onChange(trimmed);
      onSelectItem?.({
        name: trimmed,
        itemType: filterType === "service" ? "service" : "goods",
        isNew: true,
      });

      setIsOpen(false);
      onAdvanceNext?.();
    },
    [stockItems, getItemAvailability, selectItem, onChange, onSelectItem, filterType, transactionType, onEmptyRowEnter, onAdvanceNext, toast]
  );

  const openCreateDialog = useCallback(() => {
    setForm(emptyForm((value || "").trim()));
    setShowCreateDialog(true);
    setIsOpen(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        e.stopPropagation();
        openCreateDialog();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((i) => Math.min(i + 1, Math.max(0, totalOptions - 1)));
        }
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((i) => Math.max(i - 1, 0));
        }
        return;
      }

      if (e.key === "Escape") {
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
        }
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();

        // 1. If dropdown is open and a valid item is highlighted
        if (isOpen && filtered.length > 0 && highlightedIndex < filtered.length) {
          selectItem(filtered[highlightedIndex].item);
          return;
        }

        // 2. Custom item name typed or on auto-create option
        commitItem(value || "");
        return;
      }

      if (e.key === "Tab") {
        if (isOpen) {
          setIsOpen(false);
          // If typed something new, commit it automatically on Tab
          if (value && value.trim().length > 0) {
            commitItem(value);
          }
        }
      }
    },
    [isOpen, filtered, highlightedIndex, totalOptions, value, selectItem, commitItem, openCreateDialog]
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [value, isOpen]);

  useEffect(() => {
    if (listRef.current && isOpen) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, isOpen]);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (isOpen) {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const handleCreateItem = () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      toast({ title: "Error", description: "Item name is required", variant: "destructive" });
      return;
    }
    const selling = parseFloat(form.sellingPrice) || 0;
    const purchase = parseFloat(form.purchasePrice) || 0;
    if (selling < 0 || purchase < 0) {
      toast({ title: "Error", description: "Rates cannot be negative", variant: "destructive" });
      return;
    }

    const isService = form.itemType === "service";
    const defaultGroup = isService ? "Services" : stockGroups[0]?.name || "General";

    const newItem = addStockItem({
      name: trimmedName,
      itemType: form.itemType,
      stockGroup: defaultGroup,
      unit: form.unit,
      hsnCode: isService ? "" : form.hsnCode.trim().slice(0, 8),
      sacCode: isService ? form.sacCode.trim().slice(0, 8) : "",
      billingMethod: isService ? form.billingMethod : undefined,
      standardRate: selling,
      costRate: purchase,
      description: form.description || undefined,
      gstRate: form.gstRate,
      openingQty: 0,
      openingRate: purchase,
      openingValue: 0,
      currentQty: 0,
      currentRate: selling,
      currentValue: 0,
    });

    selectItem(newItem);

    toast({
      title: `${isService ? "Service" : "Goods"} Created`,
      description: `"${newItem.name}" added to catalogue.`,
    });
    setShowCreateDialog(false);
  };

  return (
    <>
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            ref={setCombinedRef}
            type="text"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            onBlur={() => {
              // Delay slightly so clicks in dropdown register before blur
              setTimeout(() => {
                if (value && value.trim()) {
                  // Only commit if still open or not yet matched
                  const trimmed = value.trim();
                  const match = stockItems.find((s) => s.name.trim().toLowerCase() === trimmed.toLowerCase());
                  if (!match) {
                    onSelectItem?.({
                      name: trimmed,
                      itemType: filterType === "service" ? "service" : "goods",
                      isNew: true,
                    });
                  }
                }
              }, 250);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              "h-9 w-full pl-8 pr-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent",
              className
            )}
            autoComplete="off"
            data-field="description-input"
          />
        </div>

        {showDropdown && (
          <ul
            ref={listRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl py-1 divide-y divide-border/40 text-foreground"
            role="listbox"
          >
            {filtered.length === 0 && hasQuery ? (
              <li
                className="flex items-center gap-2 px-3 py-2.5 text-xs text-primary bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitItem(value.trim());
                }}
              >
                <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="truncate">
                  New catalogue item: <strong className="text-foreground font-semibold">"{value.trim()}"</strong> — auto-creates on entry
                </span>
                <kbd className="ml-auto text-[9px] px-1 py-0.5 rounded bg-muted border border-border font-mono text-muted-foreground shrink-0">
                  Enter / Tab ↵
                </kbd>
              </li>
            ) : (
              filtered.map(({ item }, idx) => {
                const avail = getItemAvailability(item);
                const isService = avail.isService;
                const isLow = avail.isLowStock;
                const isOutOfStock = avail.isOutOfStock;
                const isFullyAllocated = avail.isFullyAllocated;
                const availableStock = avail.availableStock;
                const persistedStock = avail.persistedStock;
                const allocatedInOtherRows = avail.allocatedInOtherRows;
                const activeBatches = isService ? 0 : getBatchesByItem(item.id).filter((b) => b.status === "Active").length;
                const isSelected = idx === highlightedIndex;

                return (
                  <li
                    key={item.id}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isOutOfStock}
                    className={cn(
                      "flex items-center justify-between px-3 py-2 text-sm transition-colors",
                      isOutOfStock
                        ? "opacity-55 bg-muted/40 cursor-not-allowed select-none"
                        : "cursor-pointer",
                      isSelected && !isOutOfStock
                        ? "bg-accent/15 border-l-2 border-l-primary"
                        : isSelected && isOutOfStock
                        ? "bg-destructive/10 border-l-2 border-l-destructive"
                        : "border-l-2 border-l-transparent hover:bg-muted/60"
                    )}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectItem(item);
                    }}
                  >
                    <div className="flex flex-col min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {isService ? (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 bg-purple-500/10 text-purple-600 border-purple-500/30 uppercase font-bold tracking-wider"
                          >
                            Service
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-600 border-blue-500/30 uppercase font-bold tracking-wider"
                          >
                            Goods
                          </Badge>
                        )}
                        <span className={cn("font-semibold text-xs truncate", isOutOfStock ? "text-muted-foreground line-through" : "text-foreground")}>
                          {highlightMatch(item.name, value)}
                        </span>
                        {isOutOfStock && (
                          <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive font-semibold uppercase tracking-wider">
                            {isFullyAllocated ? `Allocated (${allocatedInOtherRows}/${persistedStock})` : "Out of Stock (0)"}
                          </span>
                        )}
                        {isLow && !isOutOfStock && (
                          <span className="shrink-0 flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-semibold">
                            <AlertTriangle className="h-2.5 w-2.5" /> Low Stock ({availableStock} left)
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {isService ? (
                          <>
                            <span className="font-mono text-[10px] text-purple-600 dark:text-purple-400">
                              SAC: {item.sacCode || item.hsnCode || "—"}
                            </span>
                            <span>·</span>
                            <span className="capitalize">{item.billingMethod || "one-time"}</span>
                            <span>·</span>
                            <span>GST {item.gstRate}%</span>
                          </>
                        ) : (
                          <>
                            <span className="font-mono text-[10px]">HSN: {item.hsnCode || "—"}</span>
                            <span>·</span>
                            <span>Unit: {item.unit || "Nos"}</span>
                            <span>·</span>
                            <span>GST {item.gstRate}%</span>
                          </>
                        )}
                      </span>
                    </div>

                    <div className="flex flex-col items-end shrink-0 pl-2">
                      {transactionType === "Purchase" ? (
                        <>
                          <span className="text-xs font-bold font-mono text-foreground">
                            {item.lastPurchaseRate ? `₹${Number(item.lastPurchaseRate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : (item.costRate ? `₹${Number(item.costRate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—")}
                          </span>
                          <span className="text-[9px] text-muted-foreground">
                            {item.lastPurchaseRate ? "Last Purchase" : "Cost Rate"}
                          </span>
                        </>
                      ) : (
                        <>
                          {item.lastSellingRate ? (
                            <>
                              <span className="text-xs font-bold font-mono text-emerald-600 dark:text-emerald-400">
                                ₹{Number(item.lastSellingRate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                              <span className="text-[9px] text-muted-foreground">Last Sold</span>
                            </>
                          ) : item.sellingRateVerified && item.standardRate ? (
                            <>
                              <span className="text-xs font-bold font-mono text-foreground">
                                ₹{Number(item.standardRate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                              <span className="text-[9px] text-muted-foreground">Standard</span>
                            </>
                          ) : (
                            <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium bg-amber-500/10 px-1.5 py-0.5 rounded">
                              Not Sold Yet
                            </span>
                          )}
                        </>
                      )}
                      {isService ? (
                        <span className="text-[10px] text-muted-foreground italic flex items-center gap-0.5">
                          <Zap className="h-2.5 w-2.5 text-purple-500" />
                          per {item.unit || "unit"}
                        </span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Package className="h-2.5 w-2.5 text-muted-foreground" />
                          <span
                            className={cn(
                              "text-[10px]",
                              isOutOfStock
                                ? "text-destructive font-semibold"
                                : isLow
                                ? "text-amber-600 font-semibold"
                                : "text-muted-foreground"
                            )}
                          >
                            {avail.isSales ? (
                              allocatedInOtherRows > 0 ? (
                                `${availableStock} remaining (${persistedStock} total)`
                              ) : (
                                `${availableStock} in stock`
                              )
                            ) : (
                              `${item.currentQty} ${item.unit || "in stock"}`
                            )}
                          </span>
                          {activeBatches > 0 && (
                            <span className="text-[9px] px-1 py-0.2 rounded bg-muted text-muted-foreground">
                              {activeBatches} batch{activeBatches !== 1 ? "es" : ""}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })
            )}

            {/* Seamless Auto-Create indicator for custom new items */}
            {hasQuery && !exactMatch && filtered.length > 0 && (
              <li
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-colors border-t border-border/60",
                  highlightedIndex === createOptionIndex
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                )}
                onMouseEnter={() => setHighlightedIndex(createOptionIndex)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitItem(value.trim());
                }}
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">
                  Use new item: <strong className="text-foreground font-semibold">"{value.trim()}"</strong> (auto-creates)
                </span>
                <kbd className="ml-auto text-[9px] px-1 py-0.5 rounded bg-muted border border-border font-mono text-muted-foreground shrink-0">
                  Enter / Tab
                </kbd>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Optional Full Details Dialog (Alt+N) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent
          className="sm:max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between text-base">
              <span>Create {form.itemType === "service" ? "Service" : "Goods Item"}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3.5 pt-2">
            <div>
              <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                Item Classification *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, itemType: "goods", unit: "Nos" }))}
                  className={cn(
                    "flex items-center justify-center gap-2 p-2.5 rounded-lg border text-xs font-semibold transition-all",
                    form.itemType === "goods"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted"
                  )}
                >
                  <Package className="h-4 w-4" /> Physical Goods
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, itemType: "service", unit: "Services" }))}
                  className={cn(
                    "flex items-center justify-center gap-2 p-2.5 rounded-lg border text-xs font-semibold transition-all",
                    form.itemType === "service"
                      ? "border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400"
                      : "border-border text-muted-foreground hover:bg-muted"
                  )}
                >
                  <Zap className="h-4 w-4" /> Service
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Item Name / Description *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="h-9 w-full px-3 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent"
                placeholder="e.g. Intel Core i3 Processor"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  {form.itemType === "service" ? "SAC Code" : "HSN Code"}
                </label>
                <input
                  type="text"
                  maxLength={8}
                  value={form.itemType === "service" ? form.sacCode : form.hsnCode}
                  onChange={(e) =>
                    setForm((f) =>
                      form.itemType === "service"
                        ? { ...f, sacCode: e.target.value }
                        : { ...f, hsnCode: e.target.value }
                    )
                  }
                  className="h-9 w-full px-3 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent font-mono"
                  placeholder={form.itemType === "service" ? "998311" : "84713010"}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  Unit
                </label>
                <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(form.itemType === "service" ? SERVICE_UNITS : GOODS_UNITS).map((u) => (
                      <SelectItem key={u} value={u} className="text-xs">
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  GST Rate (%)
                </label>
                <Select
                  value={String(form.gstRate)}
                  onValueChange={(v) => setForm((f) => ({ ...f, gstRate: Number(v) }))}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((r) => (
                      <SelectItem key={r} value={String(r)} className="text-xs">
                        {r}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  Standard Rate (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.sellingPrice}
                  onChange={(e) => setForm((f) => ({ ...f, sellingPrice: e.target.value }))}
                  className="h-9 w-full px-3 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleCreateItem}
                className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Create & Select
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
